/* Soft — the three controls the atoms in styles/soft.module.css did not have
   (the Settings modal needed them first, 2026-10-13): a switch, a stepper and
   a slider, drawn in the manner of the track and the field — a hole one step
   lighter than the block inside a 3px tonal rim, and what is pressed or moved
   in it raised. No accent, no outer shadow, no glow; focus is a solid ring.

     .switch   on / off: a knob in a recessed capsule, 46 x 26
     .stepper  a whole number between two raised round buttons, 36 high
     .slider   a 6px line with a raised 18px thumb; the part behind it is lit
     .value    a number on a small recessed plate, with its unit; 32 high

   A control that is not available is `disabled`: flat, faint, inert — it still
   shows where it stands. */

/* ---- switch -------------------------------------------------------------- */
.switch {
  position: relative;
  flex: none;
  width: 46px;
  height: 26px;
  margin: 0;
  padding: 0;
  border: 3px solid var(--soft-rim);
  border-radius: 999px;
  background: var(--soft-fill);
  cursor: pointer;
  transition: background-color 0.15s var(--soft-ease), border-color 0.15s var(--soft-ease);
}

/* the knob: 16 in a hole 20 high, 2 from its edge; it travels 20 */
.switch::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: var(--soft-faint);
  transition: transform 0.18s var(--soft-ease), background-color 0.15s var(--soft-ease);
}

.switch:hover:not(:disabled) {
  border-color: var(--soft-raised-hover);
}

/* on: the hole is the raised segment's tone and the knob is white */
.switch[aria-checked="true"] {
  background: var(--soft-seg-on);
  border-color: #3a3a3a;
}

.switch[aria-checked="true"]:hover:not(:disabled) {
  border-color: #474747;
}

.switch[aria-checked="true"]::after {
  background: #ffffff;
  transform: translateX(20px);
}

.switch:active:not(:disabled)::after {
  width: 18px;
}

.switch[aria-checked="true"]:active:not(:disabled)::after {
  transform: translateX(18px);
}

/* not available: no hole, a thin rim, the knob where it was left — faint, and
   a step lighter when it was left on, so the saved state still reads */
.switch:disabled {
  border-color: var(--soft-rim);
  background: transparent;
  cursor: default;
}

.switch:disabled::after {
  background: #333333;
}

.switch[aria-checked="true"]:disabled::after {
  background: #5a5a5a;
}

.switch:focus-visible {
  outline: 2px solid var(--soft-focus);
  outline-offset: 2px;
}

/* ---- stepper ------------------------------------------------------------- */
.stepper {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 2px;
  height: 36px;
  padding: 0 2px;
  border: 3px solid var(--soft-rim);
  border-radius: 999px;
  background: var(--soft-fill);
  font-family: var(--font-soft);
  transition: border-color 0.15s var(--soft-ease);
}

.stepper:hover:not([data-disabled]) {
  border-color: var(--soft-raised-hover);
}

.stepper:focus-within {
  border-color: #474747;
}

.stepBtn {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: var(--soft-seg-on);
  box-shadow: var(--soft-hl-soft);
  color: var(--soft-text-2);
  cursor: pointer;
  transition: background-color 0.15s var(--soft-ease), color 0.15s var(--soft-ease), transform 0.15s
    var(--soft-ease);
}

.stepBtn:hover:not(:disabled) {
  background: #383838;
  color: #ffffff;
}

.stepBtn:active:not(:disabled) {
  background: var(--soft-raised);
  transform: translateY(1px);
}

/* at its end of the range, or not available: bare and faint */
.stepBtn:disabled {
  background: none;
  box-shadow: none;
  color: var(--soft-faint);
  cursor: default;
}

.stepBtn:focus-visible {
  outline: 2px solid var(--soft-focus);
  outline-offset: 0;
}

/* as wide as its digits, never narrower than two */
.stepInput {
  field-sizing: content;
  min-width: 24px;
  max-width: 56px;
  margin: 0;
  padding: 0 4px;
  border: 0;
  outline: none;
  background: none;
  color: var(--soft-text);
  font-family: var(--font-soft);
  font-size: 14px;
  font-weight: var(--fw-num);
  font-variant-numeric: tabular-nums;
  line-height: 20px;
  text-align: center;
}

/* the caret is in the capsule: its rim says so (the page's own ring on every
   focused element would be a square inside a capsule) */
.stepInput:focus-visible {
  outline: none;
}

.stepInput:disabled {
  color: var(--soft-faint);
}

.stepper[data-disabled] {
  border-color: var(--soft-line);
  background: transparent;
}

/* ---- slider -------------------------------------------------------------- */
/* The input is 18 high so the whole thumb is its hit area; the line is the
   runnable track, 6. The thumb's centre travels from 9 to 9 short of the end,
   so the lit part ends at 9px + p x (width - 18px): --p is 0..1, set inline.
   The thumb and the track live on the input's OWN class: Chrome drops a rule
   whose selector compounds ::-webkit-slider-thumb with anything else. The
   ground under the thumb's ring is --soft-slider-ground (the block, unless the
   surface says it stands on something else). */
.slider {
  --sl-fill: var(--soft-text);
  --sl-line: var(--soft-rim);
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: 18px;
  margin: 0;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 999px;
  background: transparent;
  outline: none;
  cursor: pointer;
}

.slider:disabled {
  --sl-fill: #4a4a4a;
  --sl-line: var(--soft-line);
  cursor: default;
}

.slider::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    var(--sl-fill) 0 calc(9px + (100% - 18px) * var(--p, 0)),
    var(--sl-line) 0 100%
  );
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  margin-top: -6px;
  border: 4px solid var(--soft-slider-ground, var(--soft-block));
  border-radius: 999px;
  background: var(--sl-fill);
}

.slider::-moz-range-track {
  height: 6px;
  border-radius: 999px;
  background: var(--sl-line);
}

.slider::-moz-range-progress {
  height: 6px;
  border-radius: 999px;
  background: var(--sl-fill);
}

.slider::-moz-range-thumb {
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  border: 4px solid var(--soft-slider-ground, var(--soft-block));
  border-radius: 999px;
  background: var(--sl-fill);
}

.slider:focus-visible {
  outline: 2px solid var(--soft-focus);
  outline-offset: 3px;
}

/* ---- value --------------------------------------------------------------- */
.value {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 52px;
  height: 32px;
  padding: 0 9px;
  border: 3px solid var(--soft-rim);
  border-radius: 999px;
  background: var(--soft-fill);
  font-family: var(--font-soft);
  cursor: text;
  transition: border-color 0.15s var(--soft-ease);
}

.value:hover:not([data-disabled]) {
  border-color: var(--soft-raised-hover);
}

.value:focus-within {
  border-color: #474747;
}

.value input {
  field-sizing: content;
  min-width: 10px;
  max-width: 40px;
  margin: 0;
  padding: 0;
  border: 0;
  outline: none;
  background: none;
  color: var(--soft-text);
  font-family: var(--font-soft);
  font-size: 13px;
  font-weight: var(--fw-num);
  font-variant-numeric: tabular-nums;
  line-height: 18px;
  text-align: right;
}

.valueUnit {
  color: var(--soft-muted);
  font-size: 12px;
  font-weight: var(--fw-label);
  line-height: 18px;
}

.value[data-disabled] {
  border-color: var(--soft-line);
  background: transparent;
  cursor: default;
}

.value[data-disabled] input,
.value[data-disabled] .valueUnit {
  color: var(--soft-faint);
}

@media (prefers-reduced-motion: reduce) {
  .switch::after {
    transition: none;
  }
}
